import { FormController } from "$root/third-party-integrations/react-hook-form";
import { valueByPath } from "$root/utils";
import { InfoTooltip } from "$root/widgets-architecture/layout/WidgetsMapper/InfoTooltip";
import { Text, TooltipContent } from "@mdotm/shadcn/legacy/components";
import { FormField, Label, Radio, RadioGroup } from "@mdotm/shadcn/mdotm/components";
import { type Control, type FieldValues, type FormState, type Path, type PathValue, useWatch } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

/** The slice of a form this field binds to: the creation mode and the weighting method it constrains. */
type QuantityModeFormValues = FieldValues & { quantityMode: boolean; fixedWeights: boolean };

export type QuantityModeFieldProps<T extends QuantityModeFormValues> = {
	control: Control<T>;
	formState: FormState<T>;
	/** Sets the weighting method: a portfolio in quantities drifts, so choosing Quantities sets Drifting. */
	setFixedWeights: (fixedWeights: false) => void;
	/** Radio when true, read-only text otherwise. The mode is chosen only while creating. */
	edit: boolean;
	/** Whether the user's customer has the `PORTFOLIO_IN_QUANTITY` service (`canCreateInQuantities`). */
	entitled: boolean;
};

/**
 * The creation mode of a portfolio: Weights or Quantities. Editable while creating, for an entitled
 * user; read-only elsewhere. Renders nothing for a portfolio in weights when the user has no access to
 * quantities, since there is no choice to show.
 */
export function QuantityModeField<T extends QuantityModeFormValues>({
	control,
	formState,
	setFixedWeights,
	edit,
	entitled,
}: QuantityModeFieldProps<T>): React.JSX.Element | null {
	const { t } = useTranslation();
	// `T` extends QuantityModeFormValues, so this path (and its boolean value) exists on every form the field accepts.
	const name = "quantityMode" as Path<T>;
	const quantityMode = useWatch({ control, name }) as boolean;

	if (!entitled && !quantityMode) {
		return null;
	}

	return (
		<FormField
			className="mb-4 max-w-[470px]"
			label={({ htmlFor }) => (
				<div className="flex items-center gap-2 mb-px">
					<Label htmlFor={htmlFor}>{t("PORTFOLIOS.MODE")}</Label>
					<InfoTooltip align="startToEnd">
						<TooltipContent>
							<Text type="Body/M/Book">
								<Trans t={t} i18nKey="PORTFOLIOS.MODE_CREATION_TOOLTIP" components={{ b: <strong /> }} />
							</Text>
						</TooltipContent>
					</InfoTooltip>
				</div>
			)}
			error={(valueByPath(formState.errors, "quantityMode") as { message?: string } | undefined)?.message}
		>
			{edit && entitled
				? (fieldProps) => (
						<FormController
							control={control}
							name={name}
							render={({ field }) => (
								<RadioGroup
									{...fieldProps}
									value={(field.value as boolean | undefined) ?? false}
									onChange={(mode: boolean) => {
										field.onChange(mode as PathValue<T, Path<T>>);
										if (mode) {
											setFixedWeights(false);
										}
									}}
								>
									<div className="flex flex-row flex-wrap gap-4">
										<Radio value={false} data-qualifier="portfolioWizard/mainInfo/quantityMode(Weights)">
											{t("PORTFOLIOS.MODE_WEIGHTS")}
										</Radio>
										<Radio value data-qualifier="portfolioWizard/mainInfo/quantityMode(Quantities)">
											{t("PORTFOLIOS.MODE_QUANTITIES")}
										</Radio>
									</div>
								</RadioGroup>
							)}
						/>
					)
				: quantityMode
					? t("PORTFOLIOS.MODE_QUANTITIES")
					: t("PORTFOLIOS.MODE_WEIGHTS")}
		</FormField>
	);
}
